import { Injectable, inject } from '@angular/core';
import { State, Action, StateContext, Store } from '@ngxs/store';
import { tap, catchError } from 'rxjs/operators';

import {
  ResetOrderAction,
  SetOrderPaymentTypeAction,
  PlaceOrderFailedAction,
  SetSalesPersonAction,
  ClearPlaceOrderErrorAction,
  SetContractSigningMethodAction,
  SetContractSigningMethodPhoneAction,
  SetUpfrontPaymentContactNumberAction,
  SetOrderServiceIdAction,
  GenerateOrderContractAction,
  GeneratePaperContractAction,
  GenerateOtherContractAction,
  GenerateSmsContractAction,
  SetIsServiceOrderAction,
  PlaceOrderErrorAction,
  UpdateProductIdentifiersForMsisdns,
  SetOrderCaptureIdAction,
  SetBankIdMsisdnAction,
  SetBankIdBypassNote,
  ToggleIsBankIdBypassed,
  SetBankIdEmailAction,
} from './order.actions';
import { UpfrontPaymentMethod } from '@shared/enums/payment-type';
import { ContractService } from '../../services/contract.service';
import { OrderStateModel } from '@shared/models/order.model';
import { NotificationService } from '@core/services/notification.service';
import { ContractType } from '@shared/enums/contract-type';
import { DealerStateSelectors } from '@core/states/dealer/dealer-state.selectors';
import { UserSelectors } from '@core/states/user/user.selectors';
import { patch } from '@ngxs/store/operators';
import { ContractTypeData } from '../../models/contract.model';
import { throwError } from 'rxjs';
import { OrderCaptureResetAction, PlaceOrderResetAction } from '../order-capture/order-capture.actions';
import { PaymentSelectors } from '../payment/payment.selectors';

enum Messages {
  PlaceOrderFailed = 'Plassering av ordre var mislykket!',
  ContractGenerationFailed = 'Feil ved opprettelse av kontrakt!',
}

const initialOrderState: OrderStateModel = {
  contractId: null,
  orderCaptureId: null,
  orderServiceId: null,
  customerId: null,
  isExistingCustomer: false,
  salesChannel: 'Diego',
  dealerId: null,
  upfrontPaymentMethod: UpfrontPaymentMethod.NoUpfrontPayment,
  upfrontPaymentContactNumber: null,
  isLoadingOrder: false,
  externalShoppingCartId: null,
  salesPersonId: null,
  shipmentOption: null,
  isContractRequired: true,
  placeOrderError: null,
  contractSigningMethod: new ContractTypeData(),
  isServiceOrder: false,
  updatedProductIdentifierMsisdns: [],
  isBankIdBypassed: false,
};

@State<OrderStateModel>({
  name: 'order',
  defaults: initialOrderState,
})
@Injectable()
export class OrderState {
  private store = inject(Store);
  private notificationService = inject(NotificationService);
  private contractService = inject(ContractService);

  @Action(SetOrderServiceIdAction)
  public setOrderServiceId({ patchState }: StateContext<OrderStateModel>, { orderServiceId }: SetOrderServiceIdAction) {
    patchState({ orderServiceId });
  }

  @Action(SetOrderCaptureIdAction)
  public setOrderCaptureId({ patchState }: StateContext<OrderStateModel>, { orderCaptureId }: SetOrderCaptureIdAction) {
    patchState({ orderCaptureId });
  }

  @Action(SetOrderPaymentTypeAction)
  public setOrderPaymentType(
    { patchState }: StateContext<OrderStateModel>,
    { upfrontPaymentMethod }: SetOrderPaymentTypeAction
  ) {
    switch (upfrontPaymentMethod) {
      case UpfrontPaymentMethod.Vipps:
      case UpfrontPaymentMethod.CreditCard:
      case UpfrontPaymentMethod.Klarna:
        patchState({ upfrontPaymentMethod });
        break;
      case UpfrontPaymentMethod.NoUpfrontPayment:
        const productsWithUpfrontPayment = this.store.selectSnapshot(PaymentSelectors.productsWithUpfrontPayment);
        if (productsWithUpfrontPayment.amount === 0) {
          patchState({ upfrontPaymentMethod });
        }
        break;
    }
  }

  @Action(SetUpfrontPaymentContactNumberAction)
  public setUpfrontPaymentContactNumber(
    { patchState }: StateContext<OrderStateModel>,
    { upfrontPaymentContactNumber }: SetUpfrontPaymentContactNumberAction
  ) {
    patchState({ upfrontPaymentContactNumber });
  }

  @Action(SetSalesPersonAction)
  public setSalesPerson({ patchState }: StateContext<OrderStateModel>, { salesPersonId }: SetSalesPersonAction) {
    patchState({ salesPersonId });
  }

  @Action(ClearPlaceOrderErrorAction)
  public clearPlaceOrderError({ patchState }: StateContext<OrderStateModel>) {
    patchState({ placeOrderError: null });
  }

  @Action(SetContractSigningMethodAction)
  public setContractSigningMethod(
    { patchState }: StateContext<OrderStateModel>,
    { contractSigningMethod }: SetContractSigningMethodAction
  ) {
    patchState({ contractSigningMethod });
  }

  @Action(SetContractSigningMethodPhoneAction)
  public setContractSigningMethodPhone(
    { setState }: StateContext<OrderStateModel>,
    { customerPhoneNumber }: SetContractSigningMethodPhoneAction
  ) {
    setState(
      patch<OrderStateModel>({
        contractSigningMethod: patch<ContractTypeData>({
          customerPhoneNumber,
        }),
      })
    );
  }

  @Action(SetIsServiceOrderAction)
  public setIsServiceOrder({ setState }: StateContext<OrderStateModel>, { isServiceOrder }: SetIsServiceOrderAction) {
    setState(
      patch<OrderStateModel>({
        isServiceOrder,
      })
    );
  }

  @Action(GenerateOrderContractAction)
  public generateOrderContract(
    { dispatch }: StateContext<OrderStateModel>,
    { orderCaptureItem, contractSigningMethod }: GenerateOrderContractAction
  ) {
    switch (contractSigningMethod.contractType) {
      case ContractType.Sms:
        return dispatch(new GenerateSmsContractAction(orderCaptureItem.id, contractSigningMethod.customerPhoneNumber));
      case ContractType.Paper:
        return dispatch(new GeneratePaperContractAction(orderCaptureItem.id));
      case ContractType.Other:
        return dispatch(new GenerateOtherContractAction(orderCaptureItem.id));
    }
  }

  @Action(GeneratePaperContractAction)
  public generatePaperContract(
    { patchState }: StateContext<OrderStateModel>,
    { orderCaptureId }: GeneratePaperContractAction
  ) {
    return this.contractService.generatePaperContract(orderCaptureId).pipe(
      tap(value => {
        patchState({ contractId: value });
      }),
      catchError(error => {
        this.notificationService.error(Messages.ContractGenerationFailed);
        return throwError(error);
      })
    );
  }

  @Action(GenerateOtherContractAction)
  public generateOtherContract(
    { patchState }: StateContext<OrderStateModel>,
    { orderCaptureId }: GenerateOtherContractAction
  ) {
    return this.contractService.generateOtherContract(orderCaptureId).pipe(
      tap(value => {
        patchState({ contractId: value });
      }),
      catchError(error => {
        this.notificationService.error(Messages.ContractGenerationFailed);
        return throwError(error);
      })
    );
  }

  @Action(GenerateSmsContractAction)
  public generateSmsContract(
    { patchState }: StateContext<OrderStateModel>,
    { orderCaptureId, msisdn }: GenerateSmsContractAction
  ) {
    return this.contractService.generateSmsContract(orderCaptureId, msisdn).pipe(
      tap(value => {
        patchState({ contractId: value });
      }),
      catchError(error => {
        this.notificationService.error(Messages.ContractGenerationFailed);
        return throwError(error);
      })
    );
  }

  @Action(PlaceOrderFailedAction)
  public placeOrderFailed({ patchState, dispatch }: StateContext<OrderStateModel>, { errors }: PlaceOrderFailedAction) {
    patchState({ isLoadingOrder: false });
    dispatch(new PlaceOrderResetAction());

    if (errors.length > 0) {
      const [error] = errors;
      patchState({ placeOrderError: error });
    }
  }

  @Action(PlaceOrderErrorAction)
  public placeOrderError({ patchState, dispatch }: StateContext<OrderStateModel>, { errors }: PlaceOrderErrorAction) {
    patchState({ isLoadingOrder: false });
    dispatch(new OrderCaptureResetAction());

    if (errors?.length > 0) {
      const [error] = errors;
      patchState({ placeOrderError: error });
    }
  }

  @Action(ResetOrderAction)
  public resetOrder({ getState, setState }: StateContext<OrderStateModel>) {
    const { dealerId } = getState();

    // For dealers that have the "canAdministrerSellers" claim (Retail), set sales person to null,
    // they have to always actively choose sales person on the order closer page.
    // Dealers that do not have the "canAdministrerSellers" option, set logged in user to active seller
    const canAdministerSellers = this.store.selectSnapshot(DealerStateSelectors.canAdministerSellers);
    const userId = this.store.selectSnapshot(UserSelectors.userId);
    const salesPersonId = canAdministerSellers ? null : userId;

    setState({ ...initialOrderState, salesPersonId, dealerId });
  }

  @Action(UpdateProductIdentifiersForMsisdns)
  public updateProductIdentifiersForMsisdns(
    { patchState, getState }: StateContext<OrderStateModel>,
    { msisdn }: UpdateProductIdentifiersForMsisdns
  ) {
    const currentState = getState();
    const updatedMsisdns = new Set(currentState.updatedProductIdentifierMsisdns);
    updatedMsisdns.add(msisdn);
    patchState({
      updatedProductIdentifierMsisdns: Array.from(updatedMsisdns),
    });
  }

  @Action(SetBankIdMsisdnAction)
  public setBankIdMsisdn({ patchState }: StateContext<OrderStateModel>, { bankIdMsisdn }: SetBankIdMsisdnAction) {
    patchState({
      bankIdMsisdn,
    });
  }

  @Action(SetBankIdEmailAction)
  public setBankIdEmail({ patchState }: StateContext<OrderStateModel>, { bankIdEmail }: SetBankIdEmailAction) {
    patchState({
      bankIdEmail,
    });
  }

  @Action(SetBankIdBypassNote)
  public setBankIdBypassNote({ patchState }: StateContext<OrderStateModel>, { bankIdBypassNote }: SetBankIdBypassNote) {
    patchState({
      bankIdBypassNote,
    });
  }

  @Action(ToggleIsBankIdBypassed)
  public toggleIsBankIdBypassed(
    { patchState }: StateContext<OrderStateModel>,
    { isBankIdBypassed }: ToggleIsBankIdBypassed
  ) {
    patchState({
      isBankIdBypassed,
    });
  }
}
